/**
 * Global Search Dropdown Component
 *
 * Provides a search dropdown with real-time results grouped by entity type.
 * Supports keyboard navigation, debouncing, and i18n.
 */

import { t } from '../../i18n/translations.js';
import { globalSearch } from '../../services/search.js';

// Component state
const state = {
  isOpen: false,
  query: '',
  results: null,
  loading: false,
  error: null,
  selectedIndex: -1,
  flatResults: [],
};

// Debounce timer
let debounceTimer = null;
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

// Entity type icons (simple SVG)
const entityIcons = {
  contract: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>`,
  invoice: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <rect x="2" y="3" width="20" height="18" rx="2"/>
    <line x1="8" y1="7" x2="16" y2="7"/>
    <line x1="8" y1="11" x2="16" y2="11"/>
    <line x1="8" y1="15" x2="12" y2="15"/>
  </svg>`,
  vendor: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>`,
  discrepancy: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>`,
};

// Entity type colors
const entityColors = {
  contract: { bg: 'var(--primary-light)', color: 'var(--primary)' },
  invoice: { bg: 'var(--info-light)', color: 'var(--info)' },
  vendor: { bg: 'var(--accent-light)', color: 'var(--accent)' },
  discrepancy: { bg: 'var(--warning-light)', color: 'var(--warning)' },
};

// Entity type labels
const entityLabels = {
  contracts: () => t('search.contracts') || 'Contracts',
  invoices: () => t('search.invoices') || 'Invoices',
  vendors: () => t('search.vendors') || 'Vendors',
  discrepancies: () => t('search.discrepancies') || 'Discrepancies',
};

/**
 * Initialize the search dropdown
 * Attaches event listeners to the existing search input
 */
export function init() {
  const searchInput = document.getElementById('searchInput');
  if (!searchInput) {
    console.warn('[GlobalSearch] Search input not found');
    return;
  }

  // Input handler with debouncing
  searchInput.addEventListener('input', handleInput);

  // Focus handler
  searchInput.addEventListener('focus', handleFocus);

  // Keyboard navigation
  searchInput.addEventListener('keydown', handleKeydown);

  // Click outside to close
  document.addEventListener('click', handleClickOutside);

  // Create dropdown container
  createDropdownContainer();

  console.log('[GlobalSearch] Initialized');
}

/**
 * Create the dropdown container element
 */
function createDropdownContainer() {
  const searchBox = document.querySelector('.search-box');
  if (!searchBox || document.getElementById('search-dropdown')) return;

  const dropdown = document.createElement('div');
  dropdown.id = 'search-dropdown';
  dropdown.className = 'search-dropdown';
  dropdown.style.display = 'none';
  searchBox.appendChild(dropdown);
}

/**
 * Handle input changes with debouncing
 */
function handleInput(e) {
  state.query = e.target.value.trim();

  // Clear previous timer
  if (debounceTimer) clearTimeout(debounceTimer);

  if (state.query.length < MIN_QUERY_LENGTH) {
    closeDropdown();
    return;
  }

  // Show loading state
  state.loading = true;
  state.isOpen = true;
  state.error = null;
  render();

  // Debounce search
  debounceTimer = setTimeout(() => performSearch(), DEBOUNCE_MS);
}

/**
 * Handle focus on search input
 */
function handleFocus() {
  // Show dropdown if we have results
  if (state.results && state.query.length >= MIN_QUERY_LENGTH) {
    state.isOpen = true;
    render();
  }
}

/**
 * Perform the search API call
 */
async function performSearch() {
  try {
    const data = await globalSearch(state.query);
    state.results = data.results;
    state.loading = false;
    state.error = null;

    // Flatten results for keyboard navigation
    state.flatResults = flattenResults(data.results);
    state.selectedIndex = -1;

    render();
  } catch (err) {
    console.error('[GlobalSearch] Search error:', err);
    state.error = err.message || 'Search failed';
    state.loading = false;
    state.results = null;
    state.flatResults = [];
    render();
  }
}

/**
 * Flatten grouped results into a single array for keyboard navigation
 */
function flattenResults(results) {
  if (!results) return [];

  const flat = [];
  const types = ['contracts', 'invoices', 'vendors', 'discrepancies'];

  types.forEach(type => {
    if (results[type] && results[type].length > 0) {
      results[type].forEach(item => flat.push(item));
    }
  });

  return flat;
}

/**
 * Handle keyboard navigation
 */
function handleKeydown(e) {
  if (!state.isOpen) return;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      state.selectedIndex = Math.min(state.selectedIndex + 1, state.flatResults.length - 1);
      render();
      scrollToSelected();
      break;

    case 'ArrowUp':
      e.preventDefault();
      state.selectedIndex = Math.max(state.selectedIndex - 1, -1);
      render();
      scrollToSelected();
      break;

    case 'Enter':
      e.preventDefault();
      if (state.selectedIndex >= 0 && state.flatResults[state.selectedIndex]) {
        navigateToResult(state.flatResults[state.selectedIndex]);
      }
      break;

    case 'Escape':
      e.preventDefault();
      closeDropdown();
      document.getElementById('searchInput')?.blur();
      break;
  }
}

/**
 * Scroll to the selected item
 */
function scrollToSelected() {
  if (state.selectedIndex < 0) return;

  const dropdown = document.getElementById('search-dropdown');
  const selected = dropdown?.querySelector('.search-result-item.selected');
  if (selected) {
    selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

/**
 * Handle clicks outside the search area
 */
function handleClickOutside(e) {
  const searchBox = document.querySelector('.search-box');
  if (searchBox && !searchBox.contains(e.target)) {
    closeDropdown();
  }
}

/**
 * Navigate to a search result
 */
function navigateToResult(result) {
  closeDropdown();
  document.getElementById('searchInput').value = '';
  state.query = '';
  state.results = null;
  state.flatResults = [];

  // Navigate using hash
  const url = result.url.startsWith('#') ? result.url.substring(1) : result.url;
  window.location.hash = url;
}

/**
 * Close the dropdown
 */
function closeDropdown() {
  state.isOpen = false;
  state.selectedIndex = -1;
  const dropdown = document.getElementById('search-dropdown');
  if (dropdown) dropdown.style.display = 'none';
}

/**
 * Render the dropdown content
 */
function render() {
  const dropdown = document.getElementById('search-dropdown');
  if (!dropdown) return;

  if (!state.isOpen) {
    dropdown.style.display = 'none';
    return;
  }

  dropdown.style.display = 'block';
  dropdown.innerHTML = renderContent();

  // Add click handlers to result items
  dropdown.querySelectorAll('.search-result-item').forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const index = parseInt(item.dataset.index, 10);
      if (state.flatResults[index]) {
        navigateToResult(state.flatResults[index]);
      }
    });
  });
}

/**
 * Render dropdown content based on state
 */
function renderContent() {
  if (state.loading) {
    return `
      <div class="search-loading">
        <div class="loading-spinner-small"></div>
        <span>${t('common.loading') || 'Loading...'}</span>
      </div>
    `;
  }

  if (state.error) {
    return `
      <div class="search-error">
        <span>${state.error}</span>
      </div>
    `;
  }

  if (!state.results || state.flatResults.length === 0) {
    return `
      <div class="search-empty">
        <span>${t('search.noResults') || 'No results found'}</span>
      </div>
    `;
  }

  return renderGroupedResults();
}

/**
 * Render results grouped by entity type
 */
function renderGroupedResults() {
  let html = '';
  let globalIndex = 0;
  const types = ['contracts', 'invoices', 'vendors', 'discrepancies'];

  types.forEach(type => {
    const items = state.results[type];
    if (!items || items.length === 0) return;

    html += `
      <div class="search-group">
        <div class="search-group-header">${entityLabels[type]()}</div>
        ${items.map(item => {
          const isSelected = globalIndex === state.selectedIndex;
          const itemHtml = renderResultItem(item, isSelected, globalIndex);
          globalIndex++;
          return itemHtml;
        }).join('')}
      </div>
    `;
  });

  return html;
}

/**
 * Render a single result item
 */
function renderResultItem(item, isSelected, index) {
  const colors = entityColors[item.type] || entityColors.contract;
  const icon = entityIcons[item.type] || entityIcons.contract;

  return `
    <a href="${item.url}"
       class="search-result-item ${isSelected ? 'selected' : ''}"
       data-index="${index}">
      <div class="search-result-icon" style="background: ${colors.bg}; color: ${colors.color};">
        ${icon}
      </div>
      <div class="search-result-content">
        <div class="search-result-title">${highlightMatch(item.title, state.query)}</div>
        ${item.subtitle ? `<div class="search-result-subtitle">${item.subtitle}</div>` : ''}
      </div>
      <span class="search-result-badge" style="background: ${colors.bg}; color: ${colors.color};">
        ${capitalize(item.type)}
      </span>
    </a>
  `;
}

/**
 * Highlight matching text in result
 */
function highlightMatch(text, query) {
  if (!query || !text) return text || '';
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
}

/**
 * Capitalize first letter
 */
function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Cleanup function for page transitions
 */
export function destroy() {
  const searchInput = document.getElementById('searchInput');
  if (searchInput) {
    searchInput.removeEventListener('input', handleInput);
    searchInput.removeEventListener('focus', handleFocus);
    searchInput.removeEventListener('keydown', handleKeydown);
  }
  document.removeEventListener('click', handleClickOutside);

  // Clear state
  state.isOpen = false;
  state.query = '';
  state.results = null;
  state.flatResults = [];
  state.selectedIndex = -1;

  // Remove dropdown
  const dropdown = document.getElementById('search-dropdown');
  if (dropdown) dropdown.remove();
}

export default { init, destroy };
